import type { Page, Pageable } from '../../../core/api/spring-types';
import { normalizePage } from '../../../core/api/spring-types';
import type { UserDto, UserSignatureDto, UserProfileDto } from '../../../api/generated/types.gen';
import {
    getUsersReport,
    updateUserActivation,
    setPhoneCallNote,
    updateUserProfile,
    getUserProfile,
    updateUserSignature,
} from '../../../api/generated/sdk.gen';
import { handleResponse } from '@/core/api/response-handler';
import { faraDiacriticeInAdancime } from '@/shared/utils/fara-diacritice';

// Export UserDto as UserReportDTO for backward compatibility
export type UserReportDTO = UserDto;

/**
 * Users API - Thin wrapper for auto-generated SDK functions
 * Maintains backward compatibility with existing hook signatures
 */
class UsersAPI {
    /**
     * Get users report with pagination, filtering, and sorting
     * Endpoint: GET /api/users/report
     * Note: UserDto serves both as filter params and response type (backend pattern)
     */
    async getReport(
        filter: any,
        pageable: Pageable
    ): Promise<Page<any>> {
        const response = await getUsersReport({
            query: { ...filter, ...pageable } as any,
        });
        return normalizePage(handleResponse(response));
    }

    /**
     * Update user activation status and phone call notes
     * Endpoint: PATCH /api/users/update-activation
     */
    async updateActivation(data: any): Promise<void> {
        const response = await updateUserActivation({
            body: data as any,
        });
        handleResponse(response);
    }

    /**
     * Update phone call note for a specific user
     * Endpoint: PATCH /api/users/{userId}/phone-call-note
     */
    async updatePhoneNote(userId: string, phoneCallNote: string): Promise<void> {
        const response = await setPhoneCallNote({
            path: { id: userId },
            body: { phoneCallNote } as any,
        });
        handleResponse(response);
    }

    /**
     * Update parental approval signature
     * Endpoint: PATCH /api/users/signature
     * Sends complete UserSignatureDto with signature + all boolean consents
     */
    async updateSignature(data: UserSignatureDto): Promise<void> {
        const response = await updateUserSignature({
            body: data,
        });
        handleResponse(response);
    }

    /**
     * Get user profile data
     * Endpoint: GET /api/users/profile
     *
     * @param params - Optional query parameters
     * @param params.userId - User UUID (requires ADMIN/DBEST role) - if not provided, returns current user's profile
     * @param params.editionId - Edition UUID for edition-specific data (parental approval)
     * @returns UserProfileDto with nested school, parentalApproval, and schoolRequest objects
     */
    async getProfile(params?: { userId?: string; editionId?: string }): Promise<UserProfileDto> {
        const response = await getUserProfile({
            query: params,
        });
        return handleResponse(response);
    }

    /**
     * Update current user's profile data
     * Endpoint: PATCH /api/users/profile
     *
     * Diacriticele se pliaza aici, pentru toti apelantii: backendul respinge TOT corpul cu 409
     * „Diacriticele nu sunt acceptate." daca un singur text le are, iar conturile create prin
     * inscrierea cu cod au numele salvat cu diacritice — deci inlocuirea protocolului din Acasa
     * (care retrimite numele) esua fara ca profesorul sa poata corecta ceva acolo.
     */
    async updateProfile(data: any): Promise<any> {
        const response = await updateUserProfile({
            body: faraDiacriticeInAdancime(data) as any,
        });
        return handleResponse(response);
    }
}

export const usersApi = new UsersAPI();
